import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import type { FundingDashboard, Money } from '@/lib/api-admin/types';
import { FundingStatusPill } from './FundingStatusPill';

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatRate = (value: number | undefined | null): string => `${(value ?? 0).toFixed(1)}%`;

export interface FundingDashboardTopCampaignsProps {
  data: FundingDashboard;
}

/** #2 펀딩 모니터링 상위 캠페인(모금액 기준) — 각 행은 상세로 link, 달성률/참여수 노출. */
export const FundingDashboardTopCampaigns = ({ data }: FundingDashboardTopCampaignsProps) => {
  const { t } = useTranslation('funding');
  const top = data.topCampaigns ?? [];

  return (
    <section
      aria-labelledby="funding-dashboard-top-heading"
      className="rounded-lg p-5"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
      data-testid="funding-dashboard-top"
    >
      <h3
        id="funding-dashboard-top-heading"
        className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
      >
        {t('funding.dashboard.topCampaigns')}
      </h3>

      {top.length === 0 ? (
        <p className="py-6 text-center text-sm text-[color:var(--content-muted)]">
          {t('funding.dashboard.topEmpty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {top.map((c, idx) => (
            <li key={c.campaignId}>
              <Link
                to={`/funding-campaigns/${c.campaignId}`}
                className="flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-[var(--surface-1)]"
                data-testid="funding-dashboard-top-row"
              >
                <span
                  className="w-5 shrink-0 text-center text-sm font-semibold tabular-nums"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {idx + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-[color:var(--content-strong)]">
                  {c.title}
                </span>
                <FundingStatusPill status={c.status} campaignId={c.campaignId} />
                <span className="shrink-0 text-xs tabular-nums text-[color:var(--content-muted)]">
                  {t('funding.detail.pledgeCountLabel', { count: c.currentPledgeCount })}
                </span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-[color:var(--content-strong)]">
                  {formatMoney(c.currentAmount)}
                </span>
                <span
                  className="w-14 shrink-0 text-right text-xs font-semibold tabular-nums"
                  style={{ color: 'var(--status-success-fg)' }}
                >
                  {formatRate(c.achievementRate)}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
